import React from "react";
import { OptionTypeBase, OptionProps, OptionsType, ValueType } from "react-select";
import { FormikValues, FormikErrors, FormikTouched, ErrorMessage } from "formik";
import Select from "react-select";
import CreateSelect from "react-select/creatable";
import { AlertCircle } from "react-feather";
import { defaultStyles as ReactSelectStyles } from "../common/customSelect/customStyles";

export interface IOption {
	label: string;
	value: any;
}

interface ISelectProps {
	options: IOption[];
	onChange: (...args: any[]) => void;
	onBlur: (...args: any[]) => void;
	onCreate?: (inputValue: string) => void;
	customOnChange?: (option: ValueType<any>) => void;
	disabled?: boolean;
	name: string;
	multi?: boolean;
	value: any;
	errors: FormikErrors<FormikValues>;
	touched: FormikTouched<FormikValues>;
	loading?: boolean;
	isCreatable?: boolean;
	isClearable?: boolean;
	placeholder?: string;
	className?: string;
	classNamePrefix?: string;
	formatOptionLabel?: any;
	// Pagination props
	hasMorePages?: boolean;
	loadingMore?: boolean;
	onLoadMore?: () => void;
}

export const SelectField: React.FC<ISelectProps> = ({
	options,
	onBlur,
	onChange,
	multi,
	value,
	name,
	loading,
	isCreatable,
	isClearable,
	onCreate,
	placeholder,
	className,
	classNamePrefix,
	customOnChange,
	disabled = false,
	formatOptionLabel,
	hasMorePages = false,
	loadingMore = false,
	onLoadMore,
	errors,
	touched
}) => {
	const handleChange = (value: ValueType<any>) => {
		onChange(name, value);
	};

	const handleBlur = () => {
		onBlur(name, true);
	};

	const handleMenuScrollToBottom = () => {
		if (hasMorePages && !loadingMore && onLoadMore) {
			onLoadMore();
		}
	};

	const hasError = errors[name] && touched[name];
	const errorMessage = hasError ? String(errors[name]) : null;

	const selectStyles = (hasError: boolean) => {
		const baseStyles = {
			border: hasError ? "2px solid #dc3545" : "2px solid rgb(206, 219, 233)",
			paddingTop: 2,
			paddingBottom: 2,
			borderRadius: 6
		};
		return baseStyles;
	};

	return (
		<div>
			{isCreatable ? (
				<CreateSelect
					isDisabled={disabled}
					onCreateOption={onCreate}
					isClearable={isClearable}
					isLoading={loading}
					placeholder={placeholder}
					options={options}
					className={className}
					id={name}
					classNamePrefix={classNamePrefix}
					isMulti={multi}
					onChange={handleChange}
					onBlur={handleBlur}
					value={value}
					style={{ width: "60px" }}
					styles={ReactSelectStyles(false, undefined, selectStyles(!!hasError))}
					formatOptionLabel={formatOptionLabel}
					onMenuScrollToBottom={handleMenuScrollToBottom}
					aria-invalid={hasError ? "true" : "false"}
					aria-describedby={hasError ? `${name}-error` : undefined}
				/>
			) : (
				<Select
					isDisabled={disabled}
					onCreateOption={onCreate}
					isClearable={isClearable}
					isLoading={loading}
					placeholder={placeholder}
					options={options}
					id={name}
					classNamePrefix={classNamePrefix}
					isMulti={multi}
					onChange={customOnChange || handleChange}
					onBlur={handleBlur}
					value={value}
					style={{ width: "60px" }}
					styles={ReactSelectStyles(false, undefined, selectStyles(!!hasError))}
					formatOptionLabel={formatOptionLabel}
					onMenuScrollToBottom={handleMenuScrollToBottom}
					aria-invalid={hasError ? "true" : "false"}
					aria-describedby={hasError ? `${name}-error` : undefined}
				/>
			)}
			<ErrorMessage
				name={name}
				render={(error) => (
					<div
						id={`${name}-error`}
						role="alert"
						aria-live="polite"
						style={{
							display: "flex",
							alignItems: "flex-start",
							marginTop: "0.25rem",
							marginBottom: "0.5rem",
							color: "#dc3545",
							fontSize: "0.875rem",
							lineHeight: "1.25rem"
						}}
					>
						<AlertCircle
							size={14}
							style={{
								marginRight: "0.375rem",
								marginTop: "0.125rem",
								flexShrink: 0
							}}
						/>
						<span>{error}</span>
					</div>
				)}
			/>
		</div>
	);
};
